/**
 * Contrato de forma do campo `html` de um template (orcamento/contrato).
 *
 * O campo aceita DUAS formas validas:
 *   - fragmento: `<style>{css}</style>{html}` (ou so html) — como o modo
 *     Blocos historicamente salvava;
 *   - documento completo: `<!DOCTYPE html><html><head>...<style>...</style>
 *     </head><body>...</body></html>` — como um gestor cola no modo
 *     Codigo HTML.
 *
 * Antes deste modulo cada modo do TemplateEditor parseava o valor do seu
 * proprio jeito e re-serializava numa forma DIFERENTE da que entrou (Visual
 * devolvia innerHTML mutilado sem head; Blocos devolvia `<style>css</style>
 * <body>...`), entao alternar de aba corrompia o template. Aqui vive o UNICO
 * par parse/serialize que todos os modos usam: `parse(serialize(x))` e
 * estavel e a forma persistida (fragmento OU documento completo) nunca muda
 * por causa do modo em que o usuario editou.
 */

const RE_FULL_DOC = /<\s*(?:!doctype|html|head|body)[\s>]/i;

/**
 * Separa o valor em partes editaveis.
 *
 * @returns {{
 *   css: string,        // concat de TODOS os <style> (head e corpo), na ordem
 *   bodyHtml: string,   // conteudo do body SEM os <style>
 *   headExtras: string, // head sem os <style> (meta/title/link) — round-trip
 *   headLinks: string,  // so os <link> do head (fonts) — para frames de render
 *   lang: string,       // atributo lang do <html>, se houver
 *   isFullDoc: boolean, // a forma original era documento completo?
 * }}
 */
export function parseTemplateHtml(value) {
  const bruto = value || "";
  const isFullDoc = RE_FULL_DOC.test(bruto);
  const doc = new DOMParser().parseFromString(bruto, "text/html");

  const styles = [...doc.querySelectorAll("style")];
  const css = styles
    .map((s) => s.textContent || "")
    .filter((t) => t.trim())
    .join("\n");
  styles.forEach((s) => s.remove());

  const headLinks = [...doc.head.querySelectorAll("link")]
    .map((l) => l.outerHTML)
    .join("\n");
  const headExtras = doc.head.innerHTML.trim();
  const lang = doc.documentElement.getAttribute("lang") || "";
  const bodyHtml = doc.body.innerHTML;

  return { css, bodyHtml, headExtras, headLinks, lang, isFullDoc };
}

/**
 * Remonta o valor persistivel preservando a forma original (`isFullDoc`).
 * Aceita o objeto de parseTemplateHtml (headLinks e ignorado — ele e um
 * recorte de headExtras, que ja volta inteiro no documento completo).
 */
export function serializeTemplateHtml({
  css = "",
  bodyHtml = "",
  headExtras = "",
  lang = "",
  isFullDoc = false,
}) {
  // Bordas normalizadas (trim): sem isso cada ciclo parse->serialize
  // acrescentaria \n dentro do <style>/<body> e o round-trip nunca
  // estabilizaria (toda troca de aba viraria "mudanca").
  const cssLimpo = css.trim();
  const corpo = (bodyHtml || "").trim();
  const blocoCss = cssLimpo ? `<style>\n${cssLimpo}\n</style>` : "";
  if (!isFullDoc) {
    if (!blocoCss) return corpo;
    return `${blocoCss}\n${corpo}`;
  }
  const langAttr = lang ? ` lang="${lang}"` : "";
  const head = [(headExtras || '<meta charset="UTF-8">').trim(), blocoCss]
    .filter(Boolean)
    .join("\n");
  return (
    `<!DOCTYPE html>\n<html${langAttr}>\n<head>\n${head}\n</head>\n` +
    `<body>\n${corpo}\n</body>\n</html>`
  );
}
